import React from 'react';
import { TrendingUp, TrendingDown, Activity, Target } from 'lucide-react';
import { MarketCapBucket } from '../../types';

interface MarketCapAnalysisDashboardProps {
  data: MarketCapBucket[];
}

export const MarketCapAnalysisDashboard: React.FC<MarketCapAnalysisDashboardProps> = ({ data }) => {
  const totalGaps = data.reduce((sum, d) => sum + d.count, 0);
  const maxCount = Math.max(...data.map(d => d.count));

  const getColorForSuccessRate = (rate: number) => {
    if (rate >= 60) return 'text-green-400';
    if (rate >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getBarColorForSuccessRate = (rate: number) => {
    if (rate >= 60) return 'bg-green-500';
    if (rate >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="premium-stats-card">
          <div className="premium-icon-bg mb-2">
            <Activity className="h-5 w-5 text-blue-400" />
          </div>
          <p className="premium-metric-label">Totale Gap</p>
          <p className="premium-metric-value">{totalGaps.toLocaleString()}</p>
        </div>

        <div className="premium-stats-card">
          <div className="premium-icon-bg success mb-2">
            <TrendingUp className="h-5 w-5 text-green-400" />
          </div>
          <p className="premium-metric-label">Success Rate Medio</p>
          <p className="premium-metric-value">
            {(data.reduce((sum, d) => sum + d.successRate * d.count, 0) / totalGaps).toFixed(1)}%
          </p>
        </div>

        <div className="premium-stats-card">
          <div className="premium-icon-bg warning mb-2">
            <Target className="h-5 w-5 text-yellow-400" />
          </div>
          <p className="premium-metric-label">PMH Break Rate Medio</p>
          <p className="premium-metric-value">
            {(data.reduce((sum, d) => sum + d.pmhBreakRate * d.count, 0) / totalGaps).toFixed(1)}%
          </p>
        </div>

        <div className="premium-stats-card">
          <div className="premium-icon-bg error mb-2">
            <Activity className="h-5 w-5 text-red-400" />
          </div>
          <p className="premium-metric-label">Spike Medio</p>
          <p className="premium-metric-value">
            {(data.reduce((sum, d) => sum + d.avgSpike * d.count, 0) / totalGaps).toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="premium-chart-card">
        <h4 className="premium-chart-title mb-6">Distribuzione e Performance per Market Cap</h4>

        <div className="space-y-6">
          {data.map((bucket, index) => {
            const percentOfTotal = totalGaps > 0 ? (bucket.count / totalGaps) * 100 : 0;
            const countWidthPercent = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;

            return (
              <div key={index} className="border-b border-gray-700 pb-4 last:border-0">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="text-base font-semibold text-white">{bucket.label}</h5>
                    <p className="text-xs text-gray-400 mt-1">
                      {bucket.count} gap ({percentOfTotal.toFixed(1)}% del totale)
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${getColorForSuccessRate(bucket.successRate)}`}>
                      {bucket.successRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-400">Success Rate</p>
                  </div>
                </div>

                <div className="relative h-2 bg-gray-800 rounded-full overflow-hidden mb-3">
                  <div
                    className="absolute inset-y-0 left-0 bg-blue-500/60"
                    style={{ width: `${countWidthPercent}%` }}
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="h-3 w-3 text-green-400" />
                      <span className="text-xs text-gray-400">Success Rate</span>
                    </div>
                    <p className={`text-sm font-bold ${getColorForSuccessRate(bucket.successRate)}`}>
                      {bucket.successRate.toFixed(1)}%
                    </p>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Target className="h-3 w-3 text-yellow-400" />
                      <span className="text-xs text-gray-400">PMH Break</span>
                    </div>
                    <p className="text-sm font-bold text-yellow-400">
                      {bucket.pmhBreakRate.toFixed(1)}%
                    </p>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Activity className="h-3 w-3 text-blue-400" />
                      <span className="text-xs text-gray-400">Avg Spike</span>
                    </div>
                    <p className="text-sm font-bold text-blue-400">
                      {bucket.avgSpike.toFixed(1)}%
                    </p>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingDown className="h-3 w-3 text-red-400" />
                      <span className="text-xs text-gray-400">Red Close</span>
                    </div>
                    <p className="text-sm font-bold text-red-400">
                      {bucket.redCloseRate.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="premium-chart-card">
        <h4 className="premium-chart-title mb-4">Confronto Visivo Success Rate</h4>
        <div className="space-y-3">
          {data.map((bucket, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-300">{bucket.label}</span>
                <span className={`text-sm font-bold ${getColorForSuccessRate(bucket.successRate)}`}>
                  {bucket.successRate.toFixed(1)}%
                </span>
              </div>
              <div className="relative h-6 bg-gray-800 rounded-lg overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 ${getBarColorForSuccessRate(bucket.successRate)} transition-all duration-500`}
                  style={{ width: `${bucket.successRate}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
